/**
 * 패키지에 번들된 통합 문서(`docs/`)·예시 코드(`examples/`) 로더.
 *
 * dist/index.js 와 src/docs.ts 모두 패키지 루트에서 한 단계 아래이므로
 * `..` 상대 경로 해석이 빌드 전후 동일하다. npm publish 시 `files` 에
 * docs/, examples/ 가 포함되어 npx 환경에서도 같은 구조로 설치된다.
 */
import { readdir, readFile } from "node:fs/promises";
import path from "node:path";
import { fileURLToPath } from "node:url";

const PKG_ROOT = fileURLToPath(new URL("..", import.meta.url));
const DOCS_ROOT = path.join(PKG_ROOT, "docs");
const EXAMPLES_ROOT = path.join(PKG_ROOT, "examples");

export interface DocEntry {
  /** docs/ 기준 상대 경로 (예: "blog.md"). */
  path: string;
  title: string;
  description: string;
}

interface Frontmatter {
  title: string;
  description: string;
}

/** `---\ntitle: ...\ndescription: ...\n---` 형태의 단순 frontmatter 파서. */
export function parseFrontmatter(markdown: string): Frontmatter {
  const result: Frontmatter = { title: "", description: "" };
  const match = markdown.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return result;
  for (const line of match[1].split("\n")) {
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (key === "title") result.title = value;
    if (key === "description") result.description = value;
  }
  return result;
}

async function walkFiles(root: string, dir = root): Promise<string[]> {
  const entries = await readdir(dir, { withFileTypes: true });
  const files: string[] = [];
  for (const entry of entries) {
    const full = path.join(dir, entry.name);
    if (entry.isDirectory()) {
      files.push(...(await walkFiles(root, full)));
    } else {
      files.push(path.relative(root, full));
    }
  }
  return files.sort();
}

export async function listDocs(): Promise<DocEntry[]> {
  const files = (await walkFiles(DOCS_ROOT)).filter((f) => f.endsWith(".md"));
  return Promise.all(
    files.map(async (rel) => {
      const content = await readFile(path.join(DOCS_ROOT, rel), "utf8");
      const fm = parseFrontmatter(content);
      return { path: rel, title: fm.title || rel, description: fm.description };
    }),
  );
}

/** 경로 탈출(../) 방지 후 단일 문서 읽기. 없으면 null. */
export async function readDoc(relPath: string): Promise<string | null> {
  const resolved = path.resolve(DOCS_ROOT, relPath);
  if (!resolved.startsWith(DOCS_ROOT + path.sep)) return null;
  try {
    return await readFile(resolved, "utf8");
  } catch {
    return null;
  }
}

export interface SearchMatch {
  path: string;
  line: number;
  text: string;
}

/** 모든 문서에 대해 정규식 검색 (대소문자 무시). 매치 라인을 반환. */
export async function searchDocs(query: string): Promise<SearchMatch[]> {
  const regex = new RegExp(query, "i");
  const matches: SearchMatch[] = [];
  for (const doc of await listDocs()) {
    const content = await readDoc(doc.path);
    if (!content) continue;
    content.split("\n").forEach((text, idx) => {
      if (regex.test(text)) {
        matches.push({ path: doc.path, line: idx + 1, text: text.trim() });
      }
    });
  }
  return matches;
}

/**
 * Next.js 통합 예시 코드 전체를 `--- FILE: <path> ---` 헤더로 이어붙여 반환.
 * 파일 수가 적어(통합 1세트) 한 번에 주는 쪽이 에이전트 왕복을 줄인다.
 */
export async function readNextjsExampleCode(): Promise<string> {
  const root = path.join(EXAMPLES_ROOT, "nextjs");
  const files = await walkFiles(root);
  const sections = await Promise.all(
    files.map(async (rel) => {
      const content = await readFile(path.join(root, rel), "utf8");
      return `--- FILE: ${rel} ---\n${content}`;
    }),
  );
  return sections.join("\n\n");
}
